import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  getMessageThread,
  listMessageThreads,
  markThreadRead,
  payPaymentRequest,
  confirmPatchTest,
  declinePatchTest,
  sendMessage,
  uploadMessageImage,
  type ChatMessage,
  type MessageThread,
} from '../../lib/messaging-api';
import { PaymentRequestBubble } from '../components/PaymentRequestBubble';
import { PatchTestBubble } from '../components/PatchTestBubble';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  notifyMessagesChanged,
  useMessageStream,
} from '../../lib/use-message-stream';

const POLL_MS = 5000;

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatDay(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return '';
  }
}

function displayName(t: MessageThread['counterparty']): string {
  return (
    [t.first_name, t.last_name].filter(Boolean).join(' ') || 'Unknown user'
  );
}

export default function MessengerPage() {
  const { user } = useCustomerAuth();
  const myUserId = user?.id ?? null;
  const location = useLocation();
  // Deep-link entry — booking detail "Message pro" sets this so the
  // page opens straight on the right thread.
  const initialOtherId =
    (location.state as { openWithUserId?: string } | null)?.openWithUserId ??
    null;

  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);

  const [activeOtherId, setActiveOtherId] = useState<string | null>(
    initialOtherId,
  );
  const [activeCounterparty, setActiveCounterparty] =
    useState<MessageThread['counterparty'] | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [patchBusyId, setPatchBusyId] = useState<string | null>(null);

  const handlePay = async (requestId: string) => {
    if (payingId) return;
    setPayingId(requestId);
    try {
      const res = await payPaymentRequest('customer', requestId);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      toast.error('Could not start payment. Please try again.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not start payment.'));
    } finally {
      setPayingId(null);
    }
  };

  const handlePatchTest = async (id: string, action: 'confirm' | 'decline') => {
    if (patchBusyId) return;
    setPatchBusyId(id);
    try {
      if (action === 'confirm') {
        await confirmPatchTest('customer', id);
        toast.success('Patch test confirmed — added to your bookings.');
      } else {
        await declinePatchTest('customer', id);
        toast.success('Patch test declined.');
      }
      if (activeOtherId) await loadThread(activeOtherId);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update the patch test.'));
    } finally {
      setPatchBusyId(null);
    }
  };

  const listRef = useRef<HTMLDivElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const loadThreads = useCallback(async () => {
    try {
      const res = await listMessageThreads('customer');
      setThreads(res.data ?? []);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not load conversations.');
      toast.error(msg);
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  const loadThread = useCallback(async (otherId: string) => {
    try {
      const res = await getMessageThread('customer', otherId);
      setActiveCounterparty(res.counterparty);
      setMessages(res.data ?? []);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not load the conversation.');
      toast.error(msg);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
    const id = window.setInterval(loadThreads, POLL_MS);
    return () => window.clearInterval(id);
  }, [loadThreads]);

  useEffect(() => {
    if (!activeOtherId) {
      setMessages([]);
      setActiveCounterparty(null);
      return;
    }
    setMessagesLoading(true);
    void loadThread(activeOtherId);
    const id = window.setInterval(() => {
      void loadThread(activeOtherId);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [activeOtherId, loadThread]);

  // Live updates via SSE — refresh the thread list instantly, and the
  // open conversation if the new message is from the person we're
  // chatting with (the 5s poll is the fallback).
  useMessageStream('customer', (m) => {
    void loadThreads();
    if (m.sender_user_id === activeOtherId) {
      void loadThread(activeOtherId);
      void markThreadRead('customer', activeOtherId).catch(() => {});
    }
  });

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length]);

  const sortedThreads = useMemo(() => {
    return threads
      .slice()
      // Defensive: never render a thread whose counterparty is missing —
      // accessing `t.counterparty.id` on a null would crash the whole page.
      .filter((t) => t.counterparty)
      .sort(
        (a, b) =>
          new Date(b.last_message.created_at).getTime() -
          new Date(a.last_message.created_at).getTime(),
      );
  }, [threads]);

  async function handleSendImage(file: File) {
    if (!activeOtherId || uploadingImage) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setUploadingImage(true);
    try {
      const up = await uploadMessageImage('customer', file);
      await sendMessage('customer', {
        recipient_user_id: activeOtherId,
        body: '',
        message_type: 'IMAGE',
        attachment_url: up.url,
      });
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send that photo.'));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleSend() {
    if (!activeOtherId || !draft.trim() || sending) return;
    const body = draft.trim();
    setSending(true);
    const optimistic: ChatMessage = {
      id: `temp-${Date.now()}`,
      body,
      sender_user_id: myUserId ?? 'me',
      recipient_user_id: activeOtherId,
      booking_id: null,
      created_at: new Date().toISOString(),
      read_at: null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    try {
      await sendMessage('customer', {
        recipient_user_id: activeOtherId,
        body,
      });
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not send that message.');
      toast.error(msg);
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(body);
    } finally {
      setSending(false);
    }
  }

  function handleOpenThread(otherId: string) {
    setActiveOtherId(otherId);
    setThreads((prev) =>
      prev.map((t) =>
        t.counterparty.id === otherId ? { ...t, unread_count: 0 } : t,
      ),
    );
    void markThreadRead('customer', otherId)
      // Clear the header message badge straight away once the thread's read.
      .then(() => notifyMessagesChanged())
      .catch(() => {});
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-6">
          Messages
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 min-h-[520px]">
          <aside className="border border-[#ede8e9] rounded-xl bg-white overflow-hidden flex flex-col">
            <header className="px-4 py-3 border-b border-[#ede8e9] flex items-center justify-between">
              <span className="font-bold text-[#3a3335] text-[14px]">
                Conversations
              </span>
              <span className="text-[12px] text-[#908e8e]">
                {threads.length}
              </span>
            </header>

            <div className="flex-1 overflow-y-auto">
              {threadsLoading ? (
                <p className="px-4 py-6 text-sm text-[#908e8e]">Loading…</p>
              ) : sortedThreads.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#908e8e]">
                  No conversations yet. Once you book a pro you can chat with
                  them here.
                </p>
              ) : (
                <ul className="divide-y divide-[#ede8e9]">
                  {sortedThreads.map((t) => {
                    const isActive = activeOtherId === t.counterparty.id;
                    const lastFromMe =
                      t.last_message.sender_user_id === myUserId;
                    return (
                      <li
                        key={t.counterparty.id}
                        onClick={() => handleOpenThread(t.counterparty.id)}
                        className={`px-4 py-3 cursor-pointer transition-colors ${
                          isActive
                            ? 'bg-[#fbeaea]'
                            : 'hover:bg-[#fcfaf7]'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#f2eee2] flex items-center justify-center overflow-hidden shrink-0">
                            {t.counterparty.profile_pic_preview ??
                            t.counterparty.profile_pic ? (
                              <img
                                src={
                                  t.counterparty.profile_pic_preview ??
                                  t.counterparty.profile_pic ??
                                  ''
                                }
                                alt={displayName(t.counterparty)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[14px]">
                                {displayName(t.counterparty)
                                  .slice(0, 1)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-[#3a3335] text-[14px] truncate">
                                {displayName(t.counterparty)}
                              </span>
                              <span className="text-[11px] text-[#908e8e] shrink-0">
                                {formatDay(t.last_message.created_at)}
                              </span>
                            </div>
                            <p className="text-[12px] text-[#908e8e] truncate">
                              {lastFromMe ? 'You: ' : ''}
                              {t.last_message.body}
                            </p>
                          </div>
                          {t.unread_count > 0 && !isActive ? (
                            <span className="bg-[#8e2a2f] text-white text-[11px] font-bold rounded-full px-2 py-0.5 shrink-0">
                              {t.unread_count}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          <section className="border border-[#ede8e9] rounded-xl bg-white flex flex-col min-h-[520px]">
            {!activeOtherId ? (
              <div className="flex-1 flex items-center justify-center text-sm text-[#908e8e] px-6 text-center">
                Pick a conversation on the left to start chatting.
              </div>
            ) : (
              <>
                <header className="px-4 py-3 border-b border-[#ede8e9] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#f2eee2] flex items-center justify-center overflow-hidden">
                    {activeCounterparty?.profile_pic_preview ??
                    activeCounterparty?.profile_pic ? (
                      <img
                        src={
                          activeCounterparty?.profile_pic_preview ??
                          activeCounterparty?.profile_pic ??
                          ''
                        }
                        alt={displayName(activeCounterparty)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[13px]">
                        {activeCounterparty
                          ? displayName(activeCounterparty)
                              .slice(0, 1)
                              .toUpperCase()
                          : '?'}
                      </span>
                    )}
                  </div>
                  <span className="font-bold text-[#3a3335] text-[14px]">
                    {activeCounterparty
                      ? displayName(activeCounterparty)
                      : 'Conversation'}
                  </span>
                </header>

                <div
                  ref={listRef}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#fcfaf7]"
                >
                  {messagesLoading && messages.length === 0 ? (
                    <p className="text-sm text-[#908e8e]">Loading…</p>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-[#908e8e]">
                      No messages yet. Say hello!
                    </p>
                  ) : (
                    messages.map((m) => {
                      const fromMe = m.sender_user_id === myUserId;
                      if (
                        m.message_type === 'PAYMENT_REQUEST' &&
                        m.payment_request
                      ) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <PaymentRequestBubble
                              pr={m.payment_request}
                              fromMe={fromMe}
                              canPay={!fromMe}
                              onPay={() => handlePay(m.payment_request!.id)}
                              busy={payingId === m.payment_request.id}
                            />
                          </div>
                        );
                      }
                      if (
                        m.message_type === 'PATCH_TEST_REQUEST' &&
                        m.patch_test
                      ) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <PatchTestBubble
                              pt={m.patch_test}
                              fromMe={fromMe}
                              canRespond={!fromMe}
                              onConfirm={() =>
                                handlePatchTest(m.patch_test!.id, 'confirm')
                              }
                              onDecline={() =>
                                handlePatchTest(m.patch_test!.id, 'decline')
                              }
                              busy={patchBusyId === m.patch_test.id}
                            />
                          </div>
                        );
                      }
                      if (m.message_type === 'IMAGE' && m.attachment_url) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[75%] overflow-hidden rounded-2xl ${
                                fromMe
                                  ? 'bg-[#8e2a2f]'
                                  : 'bg-white border border-[#ede8e9]'
                              }`}
                            >
                              <a
                                href={m.attachment_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={m.attachment_url}
                                  alt="Shared photo"
                                  className="max-h-[260px] w-auto object-cover"
                                />
                              </a>
                              {m.body ? (
                                <p
                                  className={`px-3 py-1.5 text-[13px] ${fromMe ? 'text-white' : 'text-[#3a3335]'}`}
                                >
                                  {m.body}
                                </p>
                              ) : null}
                              <p
                                className={`px-3 pb-1.5 text-[10px] ${fromMe ? 'text-white/70' : 'text-[#908e8e]'} text-right`}
                              >
                                {formatTime(m.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={m.id}
                          className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 text-[14px] leading-[20px] ${
                              fromMe
                                ? 'bg-[#8e2a2f] text-white rounded-br-sm'
                                : 'bg-white text-[#3a3335] border border-[#ede8e9] rounded-bl-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">
                              {m.body}
                            </p>
                            <p
                              className={`mt-1 text-[10px] ${fromMe ? 'text-white/70' : 'text-[#908e8e]'} text-right`}
                            >
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <footer className="px-4 py-3 border-t border-[#ede8e9] bg-white">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleSend();
                    }}
                    className="flex items-center gap-2"
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleSendImage(f);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage || sending}
                      title="Send a photo"
                      aria-label="Send a photo"
                      className="text-[#8e2a2f] w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#fbeaea] disabled:opacity-50 shrink-0"
                    >
                      <span className="material-icons text-[20px]">
                        {uploadingImage ? 'hourglass_empty' : 'image'}
                      </span>
                    </button>
                    <input
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Type a message…"
                      className="flex-1 bg-[#fcfaf7] border border-[#ede8e9] rounded-full px-4 py-2 text-[14px] outline-none focus:border-[#8e2a2f]"
                    />
                    <button
                      type="submit"
                      disabled={!draft.trim() || sending}
                      className="bg-[#8e2a2f] text-white rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50"
                    >
                      <span className="material-icons text-[20px]">send</span>
                    </button>
                  </form>
                </footer>
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
